import React, { useState } from 'react';
import { Card } from '../Card';
import DistrictPerformanceChart from '../analytics/DistrictPerformanceChart';
import StandardsProgressionChart from '../analytics/StandardsProgressionChart';
import UserEngagementMetrics from '../analytics/UserEngagementMetrics';
import LearningPathAnalytics from '../analytics/LearningPathAnalytics';
import PageContainer from '../layouts/PageContainer';

const SuperAdminAnalyticsScreen: React.FC = () => {
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('all');
  
  const handleDateRangeChange = (range: 'week' | 'month' | 'quarter' | 'year') => {
    setDateRange(range);
  };
  
  const handleDistrictChange = (districtId: string) => {
    setSelectedDistrict(districtId);
  };
  
  return (
    <PageContainer title="Analytics Dashboard" userRole="SuperAdmin">
      <div className="flex justify-between items-center mb-6">
        
        <div className="flex space-x-4">
          <div className="relative">
            <select
              className="block appearance-none bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-primary"
              value={selectedDistrict}
              onChange={(e) => handleDistrictChange(e.target.value)}
            >
              <option value="all">All Districts</option>
              <option value="district-001">Springfield School District</option>
              <option value="district-002">Riverdale School District</option>
              <option value="district-003">Oakville School District</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
          
          <div className="flex rounded-md shadow-sm">
            <button
              className={`px-4 py-2 text-sm font-medium ${dateRange === 'week' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} border border-gray-300 rounded-l-md`}
              onClick={() => handleDateRangeChange('week')}
            >
              Week
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${dateRange === 'month' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} border-t border-b border-gray-300`}
              onClick={() => handleDateRangeChange('month')}
            >
              Month
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${dateRange === 'quarter' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} border-t border-b border-gray-300`}
              onClick={() => handleDateRangeChange('quarter')}
            >
              Quarter
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${dateRange === 'year' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} border border-gray-300 rounded-r-md`}
              onClick={() => handleDateRangeChange('year')}
            >
              Year
            </button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-4">District Performance</h2>
          <DistrictPerformanceChart dateRange={dateRange} districtId={selectedDistrict} />
        </Card>
        
        <Card className="bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Standards Progression</h2>
          <StandardsProgressionChart dateRange={dateRange} districtId={selectedDistrict} />
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-4">User Engagement</h2>
          <UserEngagementMetrics dateRange={dateRange} districtId={selectedDistrict} />
        </Card>
        
        <Card className="bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Learning Path Completion</h2>
          <LearningPathAnalytics dateRange={dateRange} districtId={selectedDistrict} />
        </Card>
      </div>
      
      <div className="grid grid-cols-1 gap-6">
        <Card className="bg-white p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Analytics Reports</h2>
            <button className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export Data
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Generated</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">District Performance Summary</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Overall performance metrics for all districts</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">May 20, 2025</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button className="text-primary hover:text-primary-dark mr-3">View</button>
                    <button className="text-primary hover:text-primary-dark">Download</button>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Student Progress Report</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Detailed student progress across all standards</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">May 18, 2025</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button className="text-primary hover:text-primary-dark mr-3">View</button>
                    <button className="text-primary hover:text-primary-dark">Download</button>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Game Usage Analytics</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Game engagement and performance statistics</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">May 15, 2025</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button className="text-primary hover:text-primary-dark mr-3">View</button>
                    <button className="text-primary hover:text-primary-dark">Download</button>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Teacher Activity Report</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Teacher engagement and assignment metrics</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">May 10, 2025</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button className="text-primary hover:text-primary-dark mr-3">View</button>
                    <button className="text-primary hover:text-primary-dark">Download</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
};

export default SuperAdminAnalyticsScreen;
